import React, { useCallback, useState } from "react";
import PropTypes from "prop-types";
import { Flex, Small } from "orcs-design-system";
import AllocationPersonSearch from "src/components/AllocationPersonSearch";
import { useModal } from "src/contexts/withModal";
import { useGroupTypes } from "src/contexts/global/WorkspaceContext";
import AddVacantRoleModal from "src/components/AddVacantRoleModal";
import AddVacantRoleForGroupModal from "src/components/AddVacantRoleForGroupModal";
import { useUpdateVacanciesInOrgChart } from "src/hooks/useUpdateVacanciesInOrgChart";
import { getGroupType } from "src/util/groupType";
import {
  useVacantRolePresetOption,
  ADD_VACANT_ROLE_OPTION_ID,
} from "src/hooks/useVacantRolePresetOption";
import { copywriting } from "src/pages/TeamPage/sub-components/TeamMembersPanel.config";
import {
  contextForCreationPropType,
  collectionNodeItemsPropType,
} from "src/components/NodeVisualizer/Nodes/propTypes";
import { SourceHandle, StyledCard, TargetHandle } from "../node.styled";
import PersonNode from "../PersonNode";
import { useAllocatePersonToGroup } from "./useAllocatePersonToGroup";

const getLabelAndPlaceholderTexts = ({ targetGroup, filterGroup }) => {
  if (targetGroup && filterGroup) {
    return {
      labelText: `Search ${filterGroup.name} to allocate a member to ${targetGroup.name}`,
    };
  }

  return {
    labelText: `Search to allocate a member to ${targetGroup.name}`,
  };
};

const CreatePersonNode = ({
  onCreate,
  contextForCreation,
  items,
  onCancel,
}) => {
  const [personToAdd, setPersonToAdd] = useState(null);
  const existingMembers = items.map((item) => item.data);

  const [, dispatch] = useModal();
  const groupTypes = useGroupTypes();
  const { presetOptions } = useVacantRolePresetOption();

  const { targetGroup, filter, filterGroup } = contextForCreation;

  const { labelText, placeHolderText } = getLabelAndPlaceholderTexts({
    targetGroup,
    filterGroup,
  });

  const { allocatePersonToGroup, isAllocationAllowed } =
    useAllocatePersonToGroup({ targetGroup, filter });

  const { onVacanciesAdded } = useUpdateVacanciesInOrgChart({
    team: targetGroup,
    groupTypes,
  });

  const addNewVacantRole = useCallback(() => {
    const onHidePersonModal = () => {
      dispatch({ type: "setModalVisible", isModalVisible: false });
    };

    const groupType = getGroupType(filterGroup, groupTypes);

    let modalComponent;

    if (groupType?.isSupply) {
      modalComponent = (
        <AddVacantRoleForGroupModal
          group={filterGroup}
          isVisible={true}
          onHideModal={onHidePersonModal}
          onVacanciesAdded={onVacanciesAdded}
          otherGroupIds={[targetGroup.id]}
        />
      );
    } else {
      modalComponent = (
        <AddVacantRoleModal
          isVisible={true}
          targetGroupId={targetGroup.id}
          groupTypesLookup={groupTypes}
          onHideModal={onHidePersonModal}
          onVacanciesAdded={onVacanciesAdded}
        />
      );
    }
    dispatch({ type: "setModalComponent", modalComponent });
  }, [targetGroup, filterGroup, groupTypes, dispatch, onVacanciesAdded]);

  const onPersonSelected = useCallback(
    async (value) => {
      if (!value) {
        return;
      }

      if (value.id === ADD_VACANT_ROLE_OPTION_ID) {
        addNewVacantRole();
        return;
      }

      const { person, alreadyInGroup } = value;

      if (person) {
        setPersonToAdd(person);

        if (!alreadyInGroup) {
          try {
            await allocatePersonToGroup(person);
          } finally {
            setPersonToAdd(false);
            onCreate(person.aggregateId);
          }
        } else {
          onCreate(person.aggregateId);
        }
      }
    },
    [allocatePersonToGroup, onCreate, addNewVacantRole]
  );

  if (personToAdd) {
    return (
      <PersonNode className="highlighted" data={personToAdd} overrideHidden />
    );
  }

  return (
    <StyledCard
      width="auto"
      minWidth="350px"
      maxWidth="350px"
      disablePointerEvents
      className="nopan nodrag"
    >
      <TargetHandle $hidden type="target" position="top" />
      <Flex flexDirection="column" gap="r">
        <Small>{labelText}</Small>
        <AllocationPersonSearch
          targetGroupId={targetGroup.id}
          existingMembers={existingMembers}
          onPersonSelected={onPersonSelected}
          onAddToGroup={() => null}
          groupTypes={groupTypes}
          disabled={!isAllocationAllowed}
          allocatedOptionLabel={copywriting.ALLOCATED}
          allocateOptionLabel={copywriting.ALLOCATE}
          autoFocus
          onBlur={onCancel}
          filter={filter}
          labelText={labelText}
          placeHolderText={placeHolderText}
          includePlaceholderRoles={true}
          presetOptions={presetOptions}
        />
      </Flex>
      <SourceHandle $hidden type="source" position="bottom" />
    </StyledCard>
  );
};

CreatePersonNode.propTypes = {
  contextForCreation: contextForCreationPropType.isRequired,
  items: collectionNodeItemsPropType.isRequired,
  onCreate: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

export default CreatePersonNode;
